import { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle, ArrowUpRight,
  ArrowDownRight, CreditCard, Receipt, Users, Briefcase, ChevronRight,
  Search, Filter, ArrowLeft, Shield, Eye, UserCheck, Wallet, Lock,
  Plus, Trash2, Star, FileText, Activity, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import API from '@/lib/api';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'transactions', label: 'Transactions', icon: Receipt },
  { id: 'campaign-spend', label: 'Campaign Spend', icon: Briefcase },
  { id: 'creator-spend', label: 'Creator Spend', icon: Users },
  { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
  { id: 'billing', label: 'Billing History', icon: FileText },
  { id: 'team-access', label: 'Team Payment Access', icon: Shield },
];

// ── Status Badge ──
function FundingStatusBadge({ status }) {
  const map = {
    awaiting_funding: 'bg-amber-50 text-amber-700 border-amber-200',
    funds_secured: 'bg-blue-50 text-blue-700 border-blue-200',
    likeness_unlocked: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    in_review: 'bg-purple-50 text-purple-700 border-purple-200',
    awaiting_creator_approval: 'bg-orange-50 text-orange-700 border-orange-200',
    approved_for_payout: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    payout_scheduled: 'bg-teal-50 text-teal-700 border-teal-200',
    paid_out: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    refunded: 'bg-red-50 text-red-600 border-red-200',
    disputed: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
  };
  const label = (status || 'unknown').replace(/_/g, ' ');
  return (
    <Badge variant="outline" data-testid={`funding-status-${status}`}
      className={`text-[10px] font-semibold capitalize whitespace-nowrap ${map[status] || 'bg-slate-50 text-slate-500'}`}>
      {label}
    </Badge>
  );
}

function fmt(n) { return n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0'; }

export default function FinanceView() {
  const [section, setSection] = useState('overview');
  const [isTeamMode, setIsTeamMode] = useState(false);

  useEffect(() => {
    API.get('/buyer/settings').then(r => {
      setIsTeamMode(r.data.settings?.workspace_mode === 'team');
    }).catch(() => {});
  }, []);

  const filteredSections = SECTIONS;

  return (
    <div data-testid="finance-view" className="flex gap-6">
      <nav className="w-48 flex-shrink-0">
        <div className="sticky top-0 space-y-0.5">
          {filteredSections.map(s => (
            <button key={s.id} data-testid={`finance-nav-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all
                ${section === s.id ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <s.icon className="w-3.5 h-3.5" />
              <span className="flex-1 text-left">{s.label}</span>
            </button>
          ))}
        </div>
      </nav>
      <div className="flex-1 min-w-0">
        {section === 'overview' && <OverviewSection />}
        {section === 'transactions' && <TransactionsSection />}
        {section === 'campaign-spend' && <CampaignSpendSection />}
        {section === 'creator-spend' && <CreatorSpendSection />}
        {section === 'payment-methods' && <PaymentMethodsSection />}
        {section === 'billing' && <BillingHistorySection />}
        {section === 'team-access' && <TeamPaymentAccessSection />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 1. OVERVIEW ──
// ═══════════════════════════════════════════════════════════
function OverviewSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/buyer/finance/overview').then(r => setData(r.data))
      .catch(() => toast.error('Failed to load finance overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCards />;
  if (!data) return <EmptyState msg="Unable to load finance data" />;

  const s = data.summary;
  const cards = [
    { label: 'Total Funded', value: fmt(s.total_funded), icon: DollarSign, accent: 'text-blue-600 bg-blue-50', delta: null },
    { label: 'Funds Held', value: fmt(s.funds_held), icon: Clock, accent: 'text-amber-600 bg-amber-50', sub: 'Pending approval' },
    { label: 'Payouts Released', value: fmt(s.payouts_released), icon: CheckCircle, accent: 'text-emerald-600 bg-emerald-50' },
    { label: 'Pending Funding', value: s.pending_funding_count, icon: AlertCircle, accent: 'text-orange-600 bg-orange-50', sub: 'Actions needed' },
    { label: 'Platform Fees', value: fmt(s.total_fees), icon: Receipt, accent: 'text-violet-600 bg-violet-50' },
    { label: 'This Month', value: fmt(s.spend_this_month), icon: TrendingUp, accent: 'text-cyan-600 bg-cyan-50' },
    { label: 'This Quarter', value: fmt(s.spend_this_quarter), icon: ArrowUpRight, accent: 'text-indigo-600 bg-indigo-50' },
  ];

  return (
    <div data-testid="finance-overview">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Finance Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((c, i) => (
          <div key={i} data-testid={`finance-card-${c.label.toLowerCase().replace(/\s/g, '-')}`}
            className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.accent}`}>
                <c.icon className="w-3.5 h-3.5" />
              </div>
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{c.label}</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{c.value}</p>
            {c.sub && <p className="text-[10px] text-slate-400 mt-0.5">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Transactions */}
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Recent Transactions</h3>
          {data.recent_transactions.length === 0 ? <p className="text-xs text-slate-400">No transactions yet</p> :
            <div className="space-y-2">
              {data.recent_transactions.map(t => (
                <div key={t.id} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                  <Avatar className="w-7 h-7"><AvatarImage src={t.creator?.avatar_url} /><AvatarFallback className="text-[10px] bg-slate-100">{(t.creator?.display_name || '?')[0]}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 truncate">{t.creator?.display_name || 'Unknown'}</p>
                    <p className="text-[10px] text-slate-400 truncate">{t.campaign?.name || 'N/A'}</p>
                  </div>
                  <FundingStatusBadge status={t.funding_status} />
                  <span className="text-xs font-semibold text-slate-700 tabular-nums">{fmt(t.amount)}</span>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Deals Awaiting Funding */}
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Awaiting Funding</h3>
          {data.awaiting_funding.length === 0 ? <p className="text-xs text-slate-400">No deals awaiting funding</p> :
            <div className="space-y-2">
              {data.awaiting_funding.map(t => (
                <div key={t.id} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 truncate">{t.creator?.display_name || 'Unknown'}</p>
                    <p className="text-[10px] text-slate-400">{t.campaign?.name || 'N/A'}</p>
                  </div>
                  <span className="text-xs font-semibold text-amber-600 tabular-nums">{fmt(t.amount)}</span>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Campaigns with highest spend */}
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Top Campaigns by Spend</h3>
          {data.top_campaigns.length === 0 ? <p className="text-xs text-slate-400">No campaign data</p> :
            <div className="space-y-2">
              {data.top_campaigns.map((c, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                  <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 truncate">{c.campaign?.name || 'Unknown'}</p>
                    <p className="text-[10px] text-slate-400">{c.campaign?.brand}</p>
                  </div>
                  <span className="text-xs font-semibold text-slate-700 tabular-nums">{fmt(c.total_spend)}</span>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Creators with highest spend */}
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Top Creators by Spend</h3>
          {data.top_creators.length === 0 ? <p className="text-xs text-slate-400">No creator data</p> :
            <div className="space-y-2">
              {data.top_creators.map((c, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                  <Avatar className="w-6 h-6"><AvatarImage src={c.creator?.avatar_url} /><AvatarFallback className="text-[10px] bg-slate-100">{(c.creator?.display_name || '?')[0]}</AvatarFallback></Avatar>
                  <p className="flex-1 text-xs font-medium text-slate-800 truncate">{c.creator?.display_name || 'Unknown'}</p>
                  <span className="text-xs font-semibold text-slate-700 tabular-nums">{fmt(c.total_spend)}</span>
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 2. TRANSACTIONS ──
// ═══════════════════════════════════════════════════════════
function TransactionsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', campaign_id: '', creator_id: '', sort_by: 'newest' });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.campaign_id) params.set('campaign_id', filters.campaign_id);
    if (filters.creator_id) params.set('creator_id', filters.creator_id);
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    API.get(`/buyer/finance/transactions?${params}`).then(r => setData(r.data))
      .catch(() => toast.error('Failed to load transactions'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (txn) => {
    setSelected(txn);
    try {
      const r = await API.get(`/buyer/finance/transactions/${txn.id}`);
      setDetail(r.data);
    } catch { toast.error('Failed to load transaction details'); }
  };

  if (loading && !data) return <SkeletonCards />;

  return (
    <div data-testid="finance-transactions">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Transactions</h2>
        <span className="text-xs text-slate-400">{data?.transactions?.length || 0} transactions</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Statuses</SelectItem>
            {(data?.filters?.statuses || []).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.campaign_id} onValueChange={v => setFilters(f => ({ ...f, campaign_id: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-campaign"><SelectValue placeholder="All Campaigns" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Campaigns</SelectItem>
            {(data?.filters?.campaigns || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.creator_id} onValueChange={v => setFilters(f => ({ ...f, creator_id: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-creator"><SelectValue placeholder="All Creators" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Creators</SelectItem>
            {(data?.filters?.creators || []).map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.sort_by} onValueChange={v => setFilters(f => ({ ...f, sort_by: v }))}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="highest">Highest Amount</SelectItem>
            <SelectItem value="funding_date">Funding Date</SelectItem>
            <SelectItem value="payout_date">Payout Date</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Creator</th>
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Campaign</th>
                <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Funding</th>
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Payout</th>
                <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Fees</th>
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Method</th>
                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.transactions || []).map(t => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`}
                  className="border-b border-slate-50 hover:bg-slate-25 cursor-pointer transition-colors"
                  onClick={() => openDetail(t)}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6"><AvatarImage src={t.creator?.avatar_url} /><AvatarFallback className="text-[9px] bg-slate-100">{(t.creator?.display_name || '?')[0]}</AvatarFallback></Avatar>
                      <span className="font-medium text-slate-800 truncate max-w-[100px]">{t.creator?.display_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 truncate max-w-[120px]">{t.campaign?.name || '—'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-800 tabular-nums">{fmt(t.amount)}</td>
                  <td className="px-3 py-2.5"><FundingStatusBadge status={t.funding_status} /></td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`text-[10px] capitalize ${t.payout_status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`}>
                      {t.payout_status || 'none'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums">{fmt(t.platform_fee)}</td>
                  <td className="px-3 py-2.5 text-slate-500 truncate max-w-[100px]">{t.payment_method_summary || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{t.funded_at ? new Date(t.funded_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2.5"><ChevronRight className="w-3.5 h-3.5 text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data?.transactions || []).length === 0 && <div className="p-8 text-center text-xs text-slate-400">No transactions found</div>}
      </div>

      {/* Transaction Detail Dialog */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setDetail(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="txn-detail-desc">
            <DialogHeader><DialogTitle className="text-base">Transaction Detail</DialogTitle></DialogHeader>
            <p id="txn-detail-desc" className="sr-only">Detailed view of a financial transaction including summary, deal info, timeline, and audit log.</p>
            {detail ? <TransactionDetail data={detail} /> : <p className="text-xs text-slate-400">Loading...</p>}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function TransactionDetail({ data }) {
  const t = data.transaction;
  const timeline = t.fund_flow_timeline || [];
  const eventLabels = {
    deal_created: 'Deal Created',
    funds_secured: 'Funds Secured',
    likeness_unlocked: 'Likeness Pack Unlocked',
    content_submitted: 'Content Submitted',
    content_in_review: 'Content In Review',
    creator_approved: 'Creator Approved',
    payout_scheduled: 'Payout Scheduled',
    payout_released: 'Payout Released',
  };

  return (
    <div className="space-y-5" data-testid="transaction-detail">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <InfoRow label="Amount" value={fmt(t.amount)} />
        <InfoRow label="Currency" value={t.currency || 'USD'} />
        <InfoRow label="Creator" value={t.creator?.display_name || '—'} />
        <InfoRow label="Campaign" value={t.campaign?.name || '—'} />
        <InfoRow label="Funding Status" value={<FundingStatusBadge status={t.funding_status} />} />
        <InfoRow label="Payout Status" value={t.payout_status || 'none'} />
        <InfoRow label="Funded" value={t.funded_at ? new Date(t.funded_at).toLocaleString() : '—'} />
        <InfoRow label="Released" value={t.released_at ? new Date(t.released_at).toLocaleString() : '—'} />
        <InfoRow label="Platform Fee" value={fmt(t.platform_fee)} />
        <InfoRow label="Net Creator Payout" value={fmt(t.net_creator_payout)} />
      </div>

      {/* Linked Deal */}
      {data.deal && (
        <div>
          <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Linked Deal</h4>
          <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-2 gap-2">
            <InfoRow label="Deal Status" value={data.deal.status} />
            <InfoRow label="Proposal" value={data.deal.proposal_status} />
            {data.license && <InfoRow label="License" value={data.license.license_id || '—'} />}
          </div>
        </div>
      )}

      {/* Fund Flow Timeline */}
      <div>
        <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Fund Flow Timeline</h4>
        <div className="space-y-0">
          {timeline.map((ev, i) => (
            <div key={i} className="flex items-start gap-3 py-2 relative">
              <div className="flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full mt-0.5 ${i === timeline.length - 1 ? 'bg-blue-500' : 'bg-slate-300'}`} />
                {i < timeline.length - 1 && <div className="w-px h-full bg-slate-200 absolute top-3 left-[5px]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800">{eventLabels[ev.event] || ev.event}</p>
                <p className="text-[10px] text-slate-400">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Processor Details */}
      <div>
        <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Processor Details</h4>
        <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-2 gap-2">
          <InfoRow label="Processor Ref" value={t.processor_ref || '—'} />
          <InfoRow label="Payment Method" value={t.payment_method_summary || '—'} />
          <InfoRow label="Payout Destination" value={t.payout_destination_status || '—'} />
        </div>
      </div>

      {/* Audit Log */}
      {data.audit_log && data.audit_log.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">Audit Log</h4>
          <div className="space-y-1.5">
            {data.audit_log.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                <span className="text-slate-400 tabular-nums whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                <span className="font-medium text-slate-600">{a.actor_name}</span>
                <span className="text-slate-400">{a.action?.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 3. CAMPAIGN SPEND ──
// ═══════════════════════════════════════════════════════════
function CampaignSpendSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);

  useEffect(() => {
    API.get('/buyer/finance/campaign-spend').then(r => setData(r.data))
      .catch(() => toast.error('Failed to load campaign spend'))
      .finally(() => setLoading(false));
  }, []);

  const openDrilldown = async (campaign) => {
    setDrilldown(campaign);
    try {
      const r = await API.get(`/buyer/finance/campaign-spend/${campaign.campaign.id}`);
      setDrilldownData(r.data);
    } catch { toast.error('Failed to load campaign details'); }
  };

  if (loading) return <SkeletonCards />;

  if (drilldown && drilldownData) {
    return (
      <div data-testid="campaign-spend-detail">
        <button onClick={() => { setDrilldown(null); setDrilldownData(null); }}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mb-4 font-medium">
          <ArrowLeft className="w-3.5 h-3.5" />Back to Campaign Spend
        </button>
        <h2 className="text-lg font-semibold text-slate-900 mb-1">{drilldownData.campaign?.name}</h2>
        <p className="text-xs text-slate-400 mb-4">{drilldownData.campaign?.brand}</p>

        {/* Creator breakdown */}
        <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Creator Breakdown</h3>
        <div className="space-y-2 mb-6">
          {drilldownData.creator_breakdown.map((cb, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-3 flex items-center gap-3">
              <Avatar className="w-8 h-8"><AvatarImage src={cb.creator?.avatar_url} /><AvatarFallback className="text-xs bg-slate-100">{(cb.creator?.display_name || '?')[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-800">{cb.creator?.display_name || 'Unknown'}</p>
                <p className="text-[10px] text-slate-400">{cb.active_count} active, {cb.completed_count} completed</p>
              </div>
              <span className="text-sm font-semibold text-slate-800 tabular-nums">{fmt(cb.total_funded)}</span>
            </div>
          ))}
        </div>

        {/* Transaction history */}
        <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Transactions</h3>
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-3 py-2 font-semibold text-slate-500">Creator</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-500">Amount</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {drilldownData.transactions.map(t => (
                <tr key={t.id} className="border-b border-slate-50">
                  <td className="px-3 py-2 text-slate-800">{t.creator?.display_name || '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums">{fmt(t.amount)}</td>
                  <td className="px-3 py-2"><FundingStatusBadge status={t.funding_status} /></td>
                  <td className="px-3 py-2 text-slate-400">{t.funded_at ? new Date(t.funded_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="finance-campaign-spend">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Campaign Spend</h2>
      <div className="space-y-2">
        {(data?.campaigns || []).map((c, i) => (
          <div key={i} data-testid={`campaign-spend-${c.campaign?.id}`}
            onClick={() => openDrilldown(c)}
            className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm cursor-pointer transition-all">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{c.campaign?.name || 'Unknown'}</p>
                <p className="text-[10px] text-slate-400">{c.campaign?.brand} &middot; {c.creator_count} creators &middot; {c.transaction_count} transactions</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-2">
              <MiniStat label="Committed" value={fmt(c.total_committed)} />
              <MiniStat label="Funded" value={fmt(c.total_funded)} />
              <MiniStat label="Held" value={fmt(c.total_held)} />
              <MiniStat label="Released" value={fmt(c.total_released)} />
              <MiniStat label="Fees" value={fmt(c.total_fees)} />
            </div>
          </div>
        ))}
        {(data?.campaigns || []).length === 0 && <EmptyState msg="No campaign spend data available" />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 4. CREATOR SPEND ──
// ═══════════════════════════════════════════════════════════
function CreatorSpendSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/buyer/finance/creator-spend').then(r => setData(r.data))
      .catch(() => toast.error('Failed to load creator spend'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCards />;

  return (
    <div data-testid="finance-creator-spend">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Creator Spend</h2>
      <div className="space-y-2">
        {(data?.creators || []).map((c, i) => (
          <div key={i} data-testid={`creator-spend-${c.creator?.user_id}`}
            className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm transition-all">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-9 h-9">
                <AvatarImage src={c.creator?.avatar_url} />
                <AvatarFallback className="text-xs bg-blue-50 text-blue-700">{(c.creator?.display_name || '?')[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{c.creator?.display_name || 'Unknown'}</p>
                <p className="text-[10px] text-slate-400">{c.active_campaign_count} campaigns &middot; {c.transaction_count} transactions</p>
              </div>
              <span className="text-base font-bold text-slate-900 tabular-nums">{fmt(c.total_funded)}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Total Released" value={fmt(c.total_released)} />
              <MiniStat label="Funds Held" value={fmt(c.funds_held)} />
              <MiniStat label="Last Transaction" value={c.last_transaction_date ? new Date(c.last_transaction_date).toLocaleDateString() : '—'} />
            </div>
          </div>
        ))}
        {(data?.creators || []).length === 0 && <EmptyState msg="No creator spend data available" />}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 5. PAYMENT METHODS ──
// ═══════════════════════════════════════════════════════════
function PaymentMethodsSection() {
  const [methods, setMethods] = useState([]);
  const [processor, setProcessor] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newMethod, setNewMethod] = useState({ method_type: 'card', label: '' });

  const load = useCallback(() => {
    API.get('/buyer/finance/payment-methods').then(r => {
      setMethods(r.data.methods);
      setProcessor(r.data.processor_status);
    }).catch(() => toast.error('Failed to load payment methods'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMethod = async () => {
    if (!newMethod.label.trim()) { toast.error('Please enter a label'); return; }
    try {
      await API.post('/buyer/finance/payment-methods', newMethod);
      toast.success('Payment method added');
      setShowAdd(false);
      setNewMethod({ method_type: 'card', label: '' });
      load();
    } catch { toast.error('Failed to add payment method'); }
  };

  const setDefault = async (id) => {
    try {
      await API.put(`/buyer/finance/payment-methods/${id}/default`);
      toast.success('Default method updated');
      load();
    } catch { toast.error('Failed to update default'); }
  };

  const remove = async (id) => {
    try {
      await API.delete(`/buyer/finance/payment-methods/${id}`);
      toast.success('Payment method removed');
      load();
    } catch { toast.error('Failed to remove method'); }
  };

  if (loading) return <SkeletonCards />;

  return (
    <div data-testid="finance-payment-methods">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Payment Methods</h2>
        <Button size="sm" className="h-8 text-xs" onClick={() => setShowAdd(true)} data-testid="add-payment-method-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add Method
        </Button>
      </div>

      {/* Processor Status */}
      <div className="bg-white rounded-xl border border-slate-100 p-4 mb-4">
        <h3 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Processor Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatusIndicator label="Payment Processor" active={processor.enabled} activeLabel="Enabled" inactiveLabel="Not Enabled" />
          <StatusIndicator label="Funding Method" active={processor.funding_method_ready} activeLabel="Ready" inactiveLabel="Not Ready" />
          <StatusIndicator label="Billing Profile" active={processor.billing_profile_ready} activeLabel="Ready" inactiveLabel="Not Ready" />
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Mode</p>
            <Badge variant="outline" className={`text-[10px] ${processor.mode === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {processor.mode || 'test'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Payment Methods List */}
      <div className="space-y-2">
        {methods.map(m => (
          <div key={m.id} data-testid={`payment-method-${m.id}`}
            className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.method_type === 'card' ? 'bg-blue-50 text-blue-600' : m.method_type === 'ach' ? 'bg-emerald-50 text-emerald-600' : 'bg-violet-50 text-violet-600'}`}>
              <CreditCard className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-slate-800">{m.label}</p>
                {m.is_default && <Badge className="bg-blue-50 text-blue-700 text-[9px] px-1.5 py-0">Default</Badge>}
              </div>
              <p className="text-[10px] text-slate-400 capitalize">{m.method_type} &middot; {m.status}</p>
            </div>
            <div className="flex items-center gap-1">
              {!m.is_default && (
                <Button variant="ghost" size="sm" className="h-7 text-[10px] text-blue-600 hover:text-blue-800" onClick={() => setDefault(m.id)} data-testid={`set-default-${m.id}`}>
                  <Star className="w-3 h-3 mr-1" />Default
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500 hover:text-red-700" onClick={() => remove(m.id)} data-testid={`remove-method-${m.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
        {methods.length === 0 && <EmptyState msg="No payment methods configured" />}
      </div>

      {/* Add Method Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm" aria-describedby="add-method-desc">
          <DialogHeader><DialogTitle className="text-sm">Add Payment Method</DialogTitle></DialogHeader>
          <p id="add-method-desc" className="sr-only">Form to add a new tokenized payment method for on-platform transactions.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={newMethod.method_type} onValueChange={v => setNewMethod(m => ({ ...m, method_type: v }))}>
                <SelectTrigger className="h-9 text-xs mt-1" data-testid="new-method-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="ach">ACH / Bank Transfer</SelectItem>
                  <SelectItem value="direct_debit">Direct Debit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Label</Label>
              <Input className="h-9 text-xs mt-1" placeholder="e.g. Visa ending 4242" data-testid="new-method-label"
                value={newMethod.label} onChange={e => setNewMethod(m => ({ ...m, label: e.target.value }))} />
            </div>
            <Button size="sm" className="w-full h-9 text-xs" onClick={addMethod} data-testid="confirm-add-method">
              Add Payment Method
            </Button>
            <p className="text-[10px] text-slate-400 text-center">Sensitive payment details are tokenized and stored securely by the payment processor.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 6. BILLING HISTORY ──
// ═══════════════════════════════════════════════════════════
function BillingHistorySection() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/buyer/finance/billing-history').then(r => setRecords(r.data.records))
      .catch(() => toast.error('Failed to load billing history'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCards />;

  const typeIcons = {
    receipt: Receipt,
    confirmation: CheckCircle,
    fee_record: DollarSign,
    statement: FileText,
  };

  return (
    <div data-testid="finance-billing-history">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Billing History</h2>
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Type</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Description</th>
              <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Date</th>
            </tr>
          </thead>
          <tbody>
            {records.map(r => {
              const Icon = typeIcons[r.type] || FileText;
              return (
                <tr key={r.id} data-testid={`billing-row-${r.id}`} className="border-b border-slate-50">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 text-slate-400" />
                      <span className="capitalize text-slate-600">{r.type?.replace(/_/g, ' ')}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{r.description}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-800 tabular-nums">{r.amount > 0 ? fmt(r.amount) : '—'}</td>
                  <td className="px-3 py-2.5 text-slate-400 font-mono text-[10px]">{r.reference}</td>
                  <td className="px-3 py-2.5 text-slate-400">{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {records.length === 0 && <div className="p-8 text-center text-xs text-slate-400">No billing records found</div>}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// ── 7. TEAM PAYMENT ACCESS ──
// ═══════════════════════════════════════════════════════════
function TeamPaymentAccessSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/buyer/finance/team-payment-access').then(r => setData(r.data))
      .catch(() => toast.error('Failed to load team access'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCards />;

  if (!data?.team_mode) {
    return (
      <div data-testid="finance-team-access" className="text-center py-16">
        <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-slate-600 mb-1">Team Mode Required</h3>
        <p className="text-xs text-slate-400 max-w-xs mx-auto">Enable Team Mode in Settings to manage team payment permissions.</p>
      </div>
    );
  }

  const permissions = [
    { key: 'manage_funding_methods', label: 'Manage Payment Methods', icon: CreditCard },
    { key: 'initiate_transactions', label: 'Fund Deals', icon: DollarSign },
    { key: 'view_transaction_history', label: 'View Transactions', icon: Eye },
    { key: 'view_billing', label: 'View Billing History', icon: FileText },
    { key: 'approve_payments', label: 'Approve Internal Payments', icon: UserCheck },
    { key: 'manage_refunds', label: 'Manage Refunds', icon: ArrowDownRight },
  ];

  return (
    <div data-testid="finance-team-access">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Team Payment Access</h2>
      <p className="text-xs text-slate-400 mb-4">View which team members have access to financial operations. Manage permissions in Settings.</p>

      {/* Permission matrix */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Member</th>
              <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Role</th>
              {permissions.map(p => (
                <th key={p.key} className="text-center px-2 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">
                  <div className="flex flex-col items-center gap-0.5">
                    <p.icon className="w-3 h-3" />
                    <span className="text-[8px] leading-tight">{p.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.members.map(m => {
              const rolePerms = getRolePermissions(m.role);
              return (
                <tr key={m.id} className="border-b border-slate-50">
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-slate-800">{m.name || m.email}</p>
                    <p className="text-[10px] text-slate-400">{m.email}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className="text-[10px] capitalize">{m.role?.replace(/_/g, ' ')}</Badge>
                  </td>
                  {permissions.map(p => (
                    <td key={p.key} className="text-center px-2 py-2.5">
                      {rolePerms.includes(p.key) || rolePerms.includes('all')
                        ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mx-auto" />
                        : <XCircle className="w-3.5 h-3.5 text-slate-200 mx-auto" />}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.members.length === 0 && <div className="p-8 text-center text-xs text-slate-400">No team members configured</div>}
      </div>
    </div>
  );
}

function getRolePermissions(role) {
  const map = {
    owner: ['manage_funding_methods', 'initiate_transactions', 'approve_payments', 'view_transaction_history', 'view_billing', 'manage_refunds'],
    admin: ['manage_funding_methods', 'initiate_transactions', 'approve_payments', 'view_transaction_history', 'view_billing', 'manage_refunds'],
    finance: ['manage_funding_methods', 'initiate_transactions', 'approve_payments', 'view_transaction_history', 'view_billing'],
    account_manager: ['initiate_transactions', 'view_transaction_history'],
    legal: ['view_transaction_history', 'view_billing'],
    read_only: ['view_transaction_history'],
  };
  return map[role] || [];
}


// ── Shared Components ──

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <div className="text-xs font-medium text-slate-800 mt-0.5">{value || '—'}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className="text-xs font-semibold text-slate-800 tabular-nums">{value}</p>
    </div>
  );
}

function StatusIndicator({ label, active, activeLabel, inactiveLabel }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        <span className="text-xs font-medium text-slate-700">{active ? activeLabel : inactiveLabel}</span>
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="text-center py-12">
      <DollarSign className="w-10 h-10 text-slate-200 mx-auto mb-2" />
      <p className="text-xs text-slate-400">{msg}</p>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-6 w-40 bg-slate-100 rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-100 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[1, 2].map(i => <div key={i} className="h-48 bg-slate-100 rounded-xl" />)}
      </div>
    </div>
  );
}
